import { createListenerMiddleware, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CoreApplicationContext, getSessionIdentity, sessionIdentitiesEqual } from "@webcore/models/coreModels.ts";
import { api } from "@webcore/services/api/api.ts";
import { AuthProvider } from "@webcore/services/api/models.ts";
import { broadcast } from "@webcore/services/CrossTabSessionChannel.ts";

export interface CoreContextState {
  coreApplicationContext: CoreApplicationContext | null;
  globalAppBlockingOperationInProgress: boolean;
  lastIdentityProvider: AuthProvider | null;
  numUpdatesInFlight: number;
  lastAuthExtensionTimestamp: number | null;
}

// Minimal store shape this slice's selectors operate on. Any app whose store registers the
// `coreContext` reducer (web-client, ops-mfe-app) satisfies this structurally, so the slice is
// shared without depending on a single app's RootState.
export interface CoreContextRootState {
  coreContext: CoreContextState;
}

// A helper function to load persisted state
const loadPersistedState = (): Partial<CoreContextState> => {
  try {
    const serializedState = localStorage.getItem('dataFabriqCoreContext');
    if (serializedState === null) {
      return {};
    }
    return JSON.parse(serializedState) as Partial<CoreContextState>;
  }
  catch (err) {
    console.error('Error reading persisted state:', err);
    return {};
  }
};

const persistedState = loadPersistedState();

const defaultInitialState: CoreContextState = {
  coreApplicationContext: null,
  globalAppBlockingOperationInProgress: false,
  lastIdentityProvider: null,
  numUpdatesInFlight: 0,
  lastAuthExtensionTimestamp: null,
};

// Initial state
const initialState: CoreContextState = {
  ...defaultInitialState,
  ...persistedState
};

const coreContextSlice = createSlice({
  name: 'coreContext',
  initialState,
  reducers: {
    initiateLoginViaIdTokenApi(state, action: PayloadAction<AuthProvider>) {
      state.coreApplicationContext = null;
      state.lastIdentityProvider = action.payload;
    },
    forgetLastIdentityProvider(state, _action: PayloadAction) {
      state.lastIdentityProvider = null;
    },
    // setActiveProjectId: (state, action: PayloadAction<string | null>) => {
    //   state.activeProjectId = action.payload;
    // },
    // setCredentials: (
    //   state,
    //   action: PayloadAction<{
    //     userContext: UserContext;
    //     provider: AuthProvider
    //   }>
    // ) => {
    //   state.userContext = action.payload.userContext;
    //   state.lastProvider = action.payload.provider;
    // },
    logout: (state) => {
      state.coreApplicationContext = null;
      state.lastAuthExtensionTimestamp = null;
      // Keeping lastProvider for potential re-login preference
    },
    incrementUpdatesInFlight(state, _action: PayloadAction) {
      ++state.numUpdatesInFlight;
    },
    decrementUpdatesInFlight(state, _action: PayloadAction) {
      --state.numUpdatesInFlight;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        action => {
          const ignoredEndpoints = ['validateInvite', 'validateSignIn', 'validateAuthSession'];

          // Check if it's a fulfilled action
          if (!action.type.endsWith('/fulfilled')) {
            return false;
          }

          const endpointName = action.meta.arg.endpointName;

          // Return true only if the endpoint is not in the ignored list
          return !ignoredEndpoints.includes(endpointName);
        },
        (state) => {
          state.lastAuthExtensionTimestamp = Date.now();
        }
      )
      .addMatcher(api.endpoints.exchangeIdTokenForDataFabriqToken.matchPending, (state) => {
      })
      .addMatcher(api.endpoints.exchangeIdTokenForDataFabriqToken.matchFulfilled, (state, action) => {
        if (!action.payload.inviteRequired && action.payload.coreApplicationContext !== null) {
          state.coreApplicationContext = action.payload.coreApplicationContext;
        }
        else {
          state.coreApplicationContext = null;
        }
      })
      .addMatcher(api.endpoints.exchangeIdTokenForDataFabriqToken.matchRejected, (state, action) => {
        state.coreApplicationContext = null;
      })
      .addMatcher(api.endpoints.signOut.matchPending, (state) => {
        state.globalAppBlockingOperationInProgress = true;
      })
      .addMatcher(api.endpoints.signOut.matchFulfilled, (state, action) => {
        state.coreApplicationContext = null;
        state.globalAppBlockingOperationInProgress = false;
      })
      .addMatcher(api.endpoints.signOut.matchRejected, (state, action) => {
        state.coreApplicationContext = null;
        state.globalAppBlockingOperationInProgress = false;
      })
      .addMatcher(api.endpoints.getCoreApplicationContext.matchFulfilled, (state, action) => {
        state.coreApplicationContext = action.payload;
      })
      .addMatcher(api.endpoints.getTeam.matchFulfilled, (state, action) => {
        if (state.coreApplicationContext) {
          state.coreApplicationContext.activeTeam = action.payload;
        }
      })
      // Every endpoint that returns a CoreApplicationContext-bearing envelope (team switch, account
      // changes, sharing/permalink endpoints, exchange-token, checkout) updates the context through
      // this single matcher. Bare-CoreApplicationContext endpoints (getCoreApplicationContext,
      // updateAppSettings, updateActivePipelineConfigurationId) are handled by their own matchers since
      // their payload has no `coreApplicationContext` wrapper.
      .addMatcher(
        (action): action is { type: string; payload: { coreApplicationContext: CoreApplicationContext } } =>
          typeof action.type === "string" &&
          action.type.endsWith("/fulfilled") &&
          typeof action.payload === "object" &&
          action.payload !== null &&
          "coreApplicationContext" in action.payload &&
          (action.payload as { coreApplicationContext: CoreApplicationContext | null }).coreApplicationContext != null,
        (state, action) => {
          state.coreApplicationContext = { ...action.payload.coreApplicationContext };
        }
      )
      .addMatcher(api.endpoints.changeActiveTeam.matchPending, (state) => {
        state.globalAppBlockingOperationInProgress = true;
      })
      .addMatcher(api.endpoints.changeActiveTeam.matchFulfilled, (state) => {
        state.globalAppBlockingOperationInProgress = false;
      })
      .addMatcher(api.endpoints.changeActiveTeam.matchRejected, (state, action) => {
        state.globalAppBlockingOperationInProgress = false;
      })
      .addMatcher(api.endpoints.createNewTeam.matchPending, (state) => {
        state.globalAppBlockingOperationInProgress = true;
      })
      .addMatcher(api.endpoints.createNewTeam.matchFulfilled, (state) => {
        state.globalAppBlockingOperationInProgress = false;
      })
      .addMatcher(api.endpoints.createNewTeam.matchRejected, (state, action) => {
        state.globalAppBlockingOperationInProgress = false;
      })
      .addMatcher(api.endpoints.updateTeamName.matchPending, (state, action) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.activeTeam.teamName = action.meta.arg.originalArgs;
      })
      .addMatcher(api.endpoints.updateTeamName.matchRejected, (state, action) => {

      })
      .addMatcher(api.endpoints.updateTeamName.matchFulfilled, (state, action) => {
      })
      .addMatcher(api.endpoints.updateTeamMemberRole.matchPending, (state, action) => {
        if (!state.coreApplicationContext) {
          return;
        }

        const { memberUserId, newRole } = action.meta.arg.originalArgs;
        const memberToUpdate = state.coreApplicationContext.activeTeam.teamMemberships.find(m => m.memberUserId === memberUserId);
        if (memberToUpdate) {
          memberToUpdate.role = newRole;
        }
      })
      .addMatcher(api.endpoints.updateGlobalTeamData.matchPending, (state, action) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.activeTeam = {
          ...state.coreApplicationContext.activeTeam,
          globalTeamData: { ...action.meta.arg.originalArgs }
        };
      })
      .addMatcher(api.endpoints.updateGlobalTeamData.matchFulfilled, (state, action) => {

      })
      .addMatcher(api.endpoints.updateGlobalTeamData.matchRejected, (state, action) => {
      })
      .addMatcher(api.endpoints.enterDraftMode.matchFulfilled, (state) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.activeTeam.firstRunExperienceState = {
          ...state.coreApplicationContext.activeTeam.firstRunExperienceState,
          dataPipelineConfigurationId: "draft"
        };
        state.coreApplicationContext.viewingDataPipelineConfigurationId = "draft";
      })
      .addMatcher(api.endpoints.publishDraft.matchFulfilled, (state) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.activeTeam.firstRunExperienceState = {
          ...state.coreApplicationContext.activeTeam.firstRunExperienceState,
          dataPipelineConfigurationId: null
        };
        state.coreApplicationContext.viewingDataPipelineConfigurationId = null;
      })
      .addMatcher(api.endpoints.discardDraft.matchFulfilled, (state) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.activeTeam.firstRunExperienceState = {
          ...state.coreApplicationContext.activeTeam.firstRunExperienceState,
          dataPipelineConfigurationId: null
        };
        state.coreApplicationContext.viewingDataPipelineConfigurationId = null;
      })
      .addMatcher(api.endpoints.updateActivePipelineConfigurationId.matchPending, (state, action) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.viewingDataPipelineConfigurationId = action.meta.arg.originalArgs.viewingDataPipelineConfigurationId;
      })
      .addMatcher(api.endpoints.updateActivePipelineConfigurationId.matchFulfilled, (state, action) => {
        state.coreApplicationContext = { ...action.payload };
      })
      .addMatcher(api.endpoints.updateFirstRunExperienceState.matchPending, (state, action) => {
        if (!state.coreApplicationContext) {
          return;
        }

        state.coreApplicationContext.activeTeam.firstRunExperienceState = action.meta.arg.originalArgs;
      })
      // .addCase(refreshToken.pending, (state) => {
      //   state.userContext = null;
      // })
      // .addCase(refreshToken.fulfilled, (state, action) => {
      //   state.userContext = action.payload;
      // })
      // .addCase(refreshToken.rejected, (state, action) => {
      //   state.userContext = null;
      // });
    //updateSubscriptionCheckoutSession
  },
});

// Export actions
export const {
  initiateLoginViaIdTokenApi,
  forgetLastIdentityProvider,
  logout,
  incrementUpdatesInFlight,
  decrementUpdatesInFlight,
} = coreContextSlice.actions;

// Export selectors

function coreApplicationContextGuard(state: CoreContextRootState) {
  if (!state.coreContext.coreApplicationContext) {
    throw new Error("user not logged in");
  }

  return state.coreContext.coreApplicationContext;
}

export const selectCoreApplicationContext = (state: CoreContextRootState) => state.coreContext.coreApplicationContext;
export const selectLastAuthExtensionTimestamp = (state: CoreContextRootState) => state.coreContext.lastAuthExtensionTimestamp;
export const selectCoreApplicationContextGuarded = (state: CoreContextRootState) => coreApplicationContextGuard(state);
export const selectUserContextGuarded = (state: CoreContextRootState) => coreApplicationContextGuard(state).userContext;

export const selectActiveTeamGuarded = (state: CoreContextRootState) => coreApplicationContextGuard(state).activeTeam;
export const selectLastIdentityProvider = (state: CoreContextRootState) => state.coreContext.lastIdentityProvider;
export const selectGlobalAppBlockingOperationInProgress = (state: CoreContextRootState) => state.coreContext.globalAppBlockingOperationInProgress;
export const selectGlobalIsUpdatingData = (state: CoreContextRootState) => state.coreContext.numUpdatesInFlight > 0;
export const selectIsUserAccountOwner = (state: CoreContextRootState) => {
  const userContext = selectUserContextGuarded(state);

  return userContext.emailAddress === userContext.activeAccountId;
}

// Export reducer
export default coreContextSlice.reducer;

// Create listener middleware for side effects
export const userContextListenerMiddleware = createListenerMiddleware();

// Broadcast session changes to other tabs
userContextListenerMiddleware.startListening({
  predicate: (_action, currentState, previousState) => {
    const prevSessionIdentity = getSessionIdentity((previousState as CoreContextRootState).coreContext.coreApplicationContext);
    const currSessionIdentity = getSessionIdentity((currentState as CoreContextRootState).coreContext.coreApplicationContext);
    return !sessionIdentitiesEqual(prevSessionIdentity, currSessionIdentity);
  },
  effect: (_action, listenerApi) => {
    const sessionIdentity = getSessionIdentity((listenerApi.getState() as CoreContextRootState).coreContext.coreApplicationContext);
    broadcast('SESSION_CHANGED', sessionIdentity);
  },
});
